import { SDK_EVENT_SET, SDK_LIMITS, SDK_ERRORS, type SdkCapability } from '@riz/contracts/sdk';
import { CapabilityRegistry } from './capabilities.ts';
import { DemoTransport, type DemoScript } from './demo-transport.ts';
import { Emitter } from './emitter.ts';
import { rejectWith, rizError, toRizError } from './errors.ts';
import { hasLocalOverride, isLocalHost, resolveEnvironment, type SdkEnvironment } from './environment.ts';
import {
  SDK_PROTOCOL_VERSION,
  type HandshakeResponse,
  type Operation,
  type SdkGame,
  type SdkPlayer,
} from './protocol.ts';
import { EventQueue } from './queue.ts';
import { DirectTransport, PostMessageTransport, type Transport } from './transport.ts';

export const SDK_VERSION = '1.0.0';

export interface InitOptions {
  gameKey: string;
  /** `auto` falls back to demo mode when no host answers; `false` fails instead. */
  sandbox?: boolean | 'auto';
  /** Origins the host may speak from. Empty accepts the first responder. */
  hostOrigins?: string[];
  /** First-party titles are same-origin with the API and skip the wrapper. */
  transport?: 'auto' | 'postmessage' | 'direct';
  /** Emit `game_paused` / `game_resumed` on tab visibility change. */
  autoPause?: boolean;
  demo?: DemoScript;
  baseUrl?: string;
}

export interface SdkSession {
  environment: SdkEnvironment;
  protocol: number;
  runId: string;
  nonce: string;
  player: SdkPlayer | null;
  game: SdkGame | null;
  limits: Record<string, number>;
  capabilities: SdkCapability[];
}

const INPUT_EVENTS = ['pointerdown', 'keydown', 'touchstart', 'wheel', 'gamepadconnected'] as const;

/**
 * Holds everything a module needs and nothing a module should own: transport
 * selection, the handshake, the capability set, the event queue, and the
 * lifecycle listeners that must be installed exactly once.
 */
export class SdkClient {
  readonly emitter = new Emitter();
  readonly capabilities = new CapabilityRegistry();

  private options: InitOptions = { gameKey: '' };
  private transport: Transport | null = null;
  private queue: EventQueue | null = null;
  private session: SdkSession | null = null;
  private connecting: Promise<SdkSession> | null = null;
  private initialised = false;
  private eventSequence = 0;
  private disposers: Array<() => void> = [];
  private heartbeat: ReturnType<typeof setInterval> | null = null;
  private runActive = false;
  private bestScore = 0;
  private activeMs = 0;
  private lastTickAt = Date.now();
  private lastInputAt = Date.now();
  private muted = false;

  get environment(): SdkEnvironment {
    return this.session?.environment ?? this.detectEnvironment();
  }

  get isDemo(): boolean {
    return this.transport?.kind === 'demo';
  }

  get currentSession(): SdkSession | null {
    return this.session;
  }

  get currentPlayer(): SdkPlayer | null {
    return this.session?.player ?? null;
  }

  get bestScoreSoFar(): number {
    return this.bestScore;
  }

  get isRunActive(): boolean {
    return this.runActive;
  }

  get isMuted(): boolean {
    return this.muted;
  }

  async init(options: InitOptions): Promise<SdkSession> {
    if (typeof options?.gameKey !== 'string' || !options.gameKey.trim()) {
      return rejectWith(SDK_ERRORS.unknownGame, 'RizGames.init requires the gameKey from Creator Studio.');
    }
    if (this.initialised && this.session && this.options.gameKey === options.gameKey) return this.session;

    this.options = { sandbox: 'auto', autoPause: true, transport: 'auto', ...options };
    this.initialised = true;
    this.installLifecycle();
    return this.connect();
  }

  connect(): Promise<SdkSession> {
    if (this.session) return Promise.resolve(this.session);
    if (this.connecting) return this.connecting;

    this.connecting = this.openSession().finally(() => {
      this.connecting = null;
    });
    return this.connecting;
  }

  requireSession(): SdkSession {
    if (!this.session) {
      throw rizError(SDK_ERRORS.notInitialized, 'Await RizGames.init({ gameKey }) before calling this.');
    }
    return this.session;
  }

  requireCapability<T>(capability: SdkCapability, moduleLabel: string): Promise<T> | null {
    return this.capabilities.require<T>(capability, moduleLabel);
  }

  /** Queued, ordered, retried. Use for anything the server records as an event. */
  async track(name: string, payload: Record<string, unknown> = {}): Promise<Record<string, unknown>> {
    if (!SDK_EVENT_SET.has(name)) {
      return rejectWith(SDK_ERRORS.invalidEvent, `Unknown Riz event "${name}".`);
    }
    if (!this.initialised) {
      return rejectWith(SDK_ERRORS.notConnected, 'Call RizGames.init({ gameKey }) before reporting events.');
    }
    await this.connect();
    const queue = this.queue;
    if (!queue) return rejectWith(SDK_ERRORS.notConnected, 'No Riz session is open.');

    const result = await queue.enqueue({
      kind: 'event',
      name,
      payload: safePayload(payload),
      eventId: uuid(),
      seq: (this.eventSequence += 1),
    });
    this.absorbResult(result);
    return result;
  }

  /** Immediate, unqueued. Use for reads and for commands that carry their own state. */
  async call(
    kind: 'query' | 'command',
    name: string,
    payload: Record<string, unknown> = {},
    timeoutMs?: number,
  ): Promise<Record<string, unknown>> {
    if (!this.initialised) {
      return rejectWith(SDK_ERRORS.notConnected, 'Call RizGames.init({ gameKey }) before calling this.');
    }
    await this.connect();
    if (!this.transport) return rejectWith(SDK_ERRORS.notConnected, 'No Riz session is open.');
    const operation: Operation = { kind, name, payload: safePayload(payload), timeoutMs };
    const result = await this.transport.send(operation).catch((error) => {
      throw toRizError(error);
    });
    this.absorbResult(result);
    return result;
  }

  notifyHost(type: string, payload: Record<string, unknown>): void {
    if (!this.transport) {
      // Loading signals fire before a session exists, so install listeners first.
      this.installLifecycle();
      this.transport = this.selectTransport();
    }
    this.transport.notify(type, payload);
  }

  markRunStarted(): void {
    this.runActive = true;
    this.bestScore = 0;
    this.activeMs = 0;
    this.lastTickAt = Date.now();
    this.lastInputAt = Date.now();
    this.startHeartbeat();
  }

  markRunEnded(): void {
    this.runActive = false;
    this.stopHeartbeat();
  }

  raiseBestScore(score: number): number {
    this.bestScore = Math.max(this.bestScore, score);
    return this.bestScore;
  }

  setMuted(muted: boolean): void {
    if (this.muted === muted) return;
    this.muted = muted;
    this.emitter.emit('mute-changed', { muted });
  }

  configureDemo(script: DemoScript): void {
    this.options.demo = { ...this.options.demo, ...script };
    if (this.transport instanceof DemoTransport) this.transport.configure(script);
  }

  demoLog() {
    return this.transport instanceof DemoTransport ? this.transport.entries() : [];
  }

  async flush(): Promise<void> {
    const queue = this.queue;
    if (!queue) return;
    while (queue.pending().length) {
      await new Promise((resolve) => setTimeout(resolve, 25));
    }
  }

  destroy(): void {
    this.stopHeartbeat();
    for (const dispose of this.disposers) dispose();
    this.disposers = [];
    this.transport?.dispose();
    this.transport = null;
    this.queue = null;
    this.session = null;
    this.connecting = null;
    this.initialised = false;
    this.runActive = false;
    this.capabilities.clear();
    this.emitter.clear();
  }

  private async openSession(): Promise<SdkSession> {
    const transport = this.selectTransport();
    this.transport = transport;

    if (this.detectEnvironment() === 'disabled') {
      const error = rizError(
        SDK_ERRORS.originNotAuthorised,
        'This build is configured to run only on Riz Games.',
      );
      this.emitter.emit('error', error);
      throw error;
    }

    let handshake: HandshakeResponse;
    try {
      handshake = await transport.connect({
        gameKey: this.options.gameKey,
        sdkVersion: SDK_VERSION,
        protocol: SDK_PROTOCOL_VERSION,
        frameOrigin: locationOrigin(),
      });
    } catch (raw) {
      const error = toRizError(raw);
      const canFallBack = transport.kind !== 'demo'
        && (this.options.sandbox === true || this.options.sandbox === 'auto');
      if (!canFallBack) {
        this.emitter.emit('error', error);
        throw error;
      }
      transport.dispose();
      return this.enterDemo(error.message);
    }

    return this.adoptSession(handshake, transport);
  }

  private async enterDemo(reason: string): Promise<SdkSession> {
    const demo = new DemoTransport(this.options.demo ?? {});
    this.transport = demo;
    announceDemo(reason);
    const handshake = await demo.connect({
      gameKey: this.options.gameKey,
      sdkVersion: SDK_VERSION,
      protocol: SDK_PROTOCOL_VERSION,
      frameOrigin: locationOrigin(),
    });
    return this.adoptSession(handshake, demo);
  }

  private adoptSession(handshake: HandshakeResponse, transport: Transport): SdkSession {
    this.capabilities.set(handshake.capabilities);
    this.queue = new EventQueue(transport, {
      maxEventsPerSession: handshake.limits.maxEventsPerSession,
      maxEventsPerMinute: handshake.limits.maxEventsPerMinute,
      maxAdEventsPerSession: handshake.limits.maxAdEventsPerSession,
    });
    transport.onSignal((signal, detail) => this.emitter.emit(signal, detail));

    this.session = {
      environment: handshake.environment,
      protocol: handshake.protocol,
      runId: handshake.runId,
      nonce: handshake.nonce,
      player: handshake.player,
      game: handshake.game,
      limits: handshake.limits,
      capabilities: handshake.capabilities as SdkCapability[],
    };

    if (handshake.deprecation) warnDeprecation(handshake.deprecation);
    this.emitter.emit('ready', this.session);
    void this.track('game_loaded', { sdkVersion: SDK_VERSION }).catch(() => {});
    return this.session;
  }

  /**
   * Deciding "should this game even attempt a real connection" cannot reuse
   * `detectEnvironment()`/`resolveEnvironment()` wholesale: that function
   * also resolves the embedded-but-unconfirmed case to `'local'` (there is
   * no session yet to refine it), which would make every embedded game fall
   * straight into the demo transport before ever trying the real one. Only
   * a genuine local-dev context (an explicit override, or the document
   * itself served from a local/loopback host) should skip the attempt;
   * "embedded" on its own means try the real handshake, and let
   * `openSession()`'s existing catch-and-fall-back-to-demo handle a host
   * that genuinely never answers.
   */
  private selectTransport(): Transport {
    if (this.transport) return this.transport;
    if (typeof window !== 'undefined'
      && (hasLocalOverride(window.location.search) || isLocalHost(window.location.hostname, window.location.protocol))) {
      return new DemoTransport(this.options.demo ?? {});
    }
    if (this.options.transport === 'direct') return new DirectTransport(this.options.baseUrl);
    if (this.options.transport === 'postmessage' || embedded()) {
      return new PostMessageTransport(window, this.options.hostOrigins ?? []);
    }
    return new DirectTransport(this.options.baseUrl);
  }

  private detectEnvironment(): SdkEnvironment {
    if (typeof window === 'undefined') return 'local';
    return resolveEnvironment({
      location: window.location,
      embedded: embedded(),
      hostDeclared: this.session?.environment ?? undefined,
      sitelockEnabled: this.session?.game?.sitelock ?? false,
    });
  }

  private absorbResult(result: Record<string, unknown>): void {
    if (typeof result.score === 'number') this.raiseBestScore(result.score);
    if (typeof result.rpBalance === 'number') {
      if (this.session?.player) this.session.player.rpBalance = result.rpBalance;
      this.emitter.emit('progress', {
        rpBalance: result.rpBalance,
        rpEarned: typeof result.rpEarned === 'number' ? result.rpEarned : 0,
        rewardsPaused: result.rewardsPaused === true,
      });
    }
  }

  private installLifecycle(): void {
    if (this.disposers.length || typeof window === 'undefined') return;

    const markInput = () => {
      this.lastInputAt = Date.now();
    };
    for (const name of INPUT_EVENTS) this.listen(window, name, markInput, { passive: true });

    this.listen(document, 'visibilitychange', () => {
      this.tickActive();
      if (!this.runActive || this.options.autoPause === false) return;
      const hidden = document.visibilityState === 'hidden';
      void this.track(hidden ? 'game_paused' : 'game_resumed', {
        reason: hidden ? 'tab_hidden' : 'tab_visible',
      }).catch(() => {});
    });

    const flushOnExit = () => this.flushOnExit();
    this.listen(window, 'pagehide', flushOnExit);
    this.listen(window, 'beforeunload', flushOnExit);
  }

  private listen(
    target: EventTarget,
    name: string,
    handler: EventListener,
    options?: AddEventListenerOptions,
  ): void {
    target.addEventListener(name, handler, options);
    this.disposers.push(() => target.removeEventListener(name, handler, options));
  }

  private startHeartbeat(): void {
    if (this.heartbeat || this.isDemo) return;
    this.lastTickAt = Date.now();
    this.heartbeat = setInterval(() => {
      this.tickActive();
      if (!this.runActive || this.activeMs < 1_000) return;
      const activeMs = this.activeMs;
      this.activeMs = 0;
      void this.track('session_heartbeat', { activeMs }).catch(() => {});
    }, SDK_LIMITS.heartbeatMs);
  }

  private stopHeartbeat(): void {
    if (!this.heartbeat) return;
    clearInterval(this.heartbeat);
    this.heartbeat = null;
  }

  /** Counts only time the tab was visible and the player was recently active. */
  private tickActive(): void {
    const now = Date.now();
    const delta = now - this.lastTickAt;
    this.lastTickAt = now;
    const visible = typeof document === 'undefined' || document.visibilityState !== 'hidden';
    if (!visible || now - this.lastInputAt > SDK_LIMITS.idleAfterMs) return;
    this.activeMs += Math.min(delta, SDK_LIMITS.heartbeatMs * 2);
  }

  /**
   * A player closing the tab mid-run would otherwise leave the run open until
   * it expired, so the queue drains through a beacon and a synthetic
   * `game_ended` closes the run at the score reached.
   */
  private flushOnExit(): void {
    if (!this.transport || !this.queue) return;
    this.tickActive();

    const outstanding = this.queue.pending();
    if (this.activeMs >= 1_000) {
      outstanding.push({
        kind: 'event',
        name: 'session_heartbeat',
        payload: { activeMs: this.activeMs },
        eventId: uuid(),
        seq: (this.eventSequence += 1),
      });
      this.activeMs = 0;
    }
    if (this.runActive) {
      outstanding.push({
        kind: 'event',
        name: 'game_ended',
        payload: { score: this.bestScore, reason: 'player_left' },
        eventId: uuid(),
        seq: (this.eventSequence += 1),
      });
      this.runActive = false;
    }
    if (outstanding.length) this.transport.beacon(outstanding);
  }
}

export function embedded(): boolean {
  try {
    return typeof window !== 'undefined' && window.parent !== window;
  } catch {
    return true;
  }
}

export function locationOrigin(): string {
  try {
    return window.location.origin || `${window.location.protocol}//${window.location.host}`;
  } catch {
    return '';
  }
}

export function uuid(): string {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch {
    // Fall through to the timestamp form.
  }
  return `r-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

const warned = new Set<string>();

export function warnOnce(key: string, message: string): void {
  if (warned.has(key)) return;
  warned.add(key);
  if (typeof console !== 'undefined' && console.warn) console.warn(`[RizGames] ${message}`);
}

/** Clones, drops anything unserialisable, and trims to the payload ceiling. */
export function safePayload(payload: unknown): Record<string, unknown> {
  if (!payload || typeof payload !== 'object') return {};
  let clone: Record<string, unknown>;
  try {
    clone = JSON.parse(JSON.stringify(payload)) as Record<string, unknown>;
  } catch {
    warnOnce('payload-cycle', 'An event payload could not be serialised and was dropped.');
    return {};
  }
  if (JSON.stringify(clone).length <= SDK_LIMITS.maxPayloadBytes) return clone;

  warnOnce('payload-size', `Event payloads over ${SDK_LIMITS.maxPayloadBytes} bytes are truncated.`);
  const trimmed: Record<string, unknown> = {};
  let size = 0;
  for (const [key, value] of Object.entries(clone)) {
    const chunk = JSON.stringify(value)?.length ?? 0;
    if (size + chunk > SDK_LIMITS.maxPayloadBytes) break;
    size += chunk;
    trimmed[key] = value;
  }
  return trimmed;
}

function announceDemo(reason: string) {
  if (typeof console === 'undefined' || !console.info) return;
  console.info(
    `%c[RizGames] DEMO MODE%c ${reason} Nothing is recorded and RP is simulated.`,
    'background:#39D6C4;color:#05070a;font-weight:bold;padding:2px 6px;border-radius:4px',
    'color:inherit',
  );
}

function warnDeprecation(deprecation: { message: string; sunsetAt: string }) {
  warnOnce('deprecation', `${deprecation.message} This SDK version stops working on ${deprecation.sunsetAt}.`);
}
